import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, costFormater, ListItem, ListItemType, Text } from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { REGEXPS } from '@/common';
import { ValuesMap } from '@/common/types';
import colors from '@/styles/colors';
import theme from '@/styles/theme';

import { useCurrentInvestorHoldingsStore } from '../AutoInvestment/SetupAutoInvestments/AutoSelfDirectedInvestments/store';
import { Donut, InvestmentStockItem } from '../investments.types';
import { useInvestmentsHoldingsQuery } from '../queries';
import { FundPayload } from '../SelfDirected/SelfDirected.types';

import { useGetFundIcon } from '.';

const MAX_FUND_COUNT = 12;
const CASH_KEY = 'CASH';
const NULL_KEY = 'OTHER';
export const IconWrapper = styled(Box)`
  &:hover {
    svg {
      cursor: pointer;
      fill: ${theme.colors.iconAccent};
    }
  }
`;

const MAX_GROUPS_DISPLAYED = 12;
export interface CategoryStockItem {
  id: string;
  key: string;
  ticker: string;
  title?: string;
  logo?: string;
  color?: string;
  category?: string;
  categoryTitle?: string;
  researchURL?: string;
  shares?: number;
  sharePrice?: number;
  value: number;
  election: number;
}
export interface DonutGroupItem {
  key: string;
  title: string;
  items: CategoryStockItem[];
  color: string;
  value: number;
  election: number;
  hint: React.ReactNode;
}
const OTHER_KEY = 'OTHER';
const SELL_TRANSACTION_KEY = 'SELL';
export default (accountId?: string) => {
  const {
    data,
    isSuccess,
    details,
    lastDateUpdate,
    isInvestorAccountExists,
    isLoading: isHoldingLoading,
  } = useInvestmentsHoldingsQuery(accountId);
  const setCurrentHoldings = useCurrentInvestorHoldingsStore((state) => state.handleSetHoldings);
  const handleGetFundIcon = useGetFundIcon();
  const formattedList = useMemo<ListItem[]>(
    () =>
      data.slice().map((item, index) => {
        return {
          id: `${item.symbol} ${index}`,
          rowStyle: {
            boxShadow: 'none',
          },
          fields: [
            {
              key: 'stockQuote',
              type: ListItemType.Node,
              title: '',
              node: (
                <Box
                  width="12px"
                  height="12px"
                  round="smallRound"
                  background={theme.colors.stocksColors[index] || theme.colors.fund12}
                />
              ),
              flex: -1,
            },
            {
              key: 'icon',
              type: ListItemType.Node,
              title: '',
              node: handleGetFundIcon(item),
              fieldStyle: {
                marginLeft: theme.spacings.xs,
                marginRight: theme.spacings.xs,
                width: theme.spacings.xl,
              },
            },
            {
              key: 'name',
              type: ListItemType.Text,
              ellipsisMode: true,
              title: item.name,
              flex: 10,
            },
            {
              key: 'receivedAmount',
              type: ListItemType.Node,
              title: '',
              flex: 3,
              node: <Box direction="row" align="center" />,
            },
            {
              key: 'currentAmount',
              type: ListItemType.Node,
              title: '',
              flex: 3,
              fieldStyle: {
                marginLeft: theme.spacings.s,
                minWidth: theme.spacings.l,
              },
              node: (
                <Text textAlign="end" weight="bold">
                  {costFormater(item.side === SELL_TRANSACTION_KEY ? item.amount * -1 : item.amount, true)}
                </Text>
              ),
            },
          ],
        };
      }),
    [data, handleGetFundIcon]
  );

  const getGroupedData = useCallback(
    (currentStocks: InvestmentStockItem[], sum: number, zeroless?: boolean) => {
      let filtered = zeroless ? _.filter(currentStocks, (item) => item.amount !== 0) : currentStocks;

      const otherItemSum = filtered
        .filter((item) => item.isOther)
        .reduce((result, item) => result + item.amount, 0);
      const otherItem = {
        id: 'other',
        stockQuote: 'Other',
        amount: otherItemSum,
        color: colors.stocksColors[MAX_FUND_COUNT - 1],

        percentage: `${otherItemSum ? _.ceil((otherItemSum / sum) * 100, 2) : 0}%`,
      };
      const firstOtherIndex = filtered.findIndex((item) => item.isOther);
      if (firstOtherIndex !== -1) {
        filtered.splice(firstOtherIndex, 0, otherItem);
      }
      filtered = filtered.filter((item) => !item.isOther);

      return filtered;
    },
    []
  );
  const donutData = useMemo(() => {
    const sums = data.reduce(
      (result, item) => ({
        holdings: result.holdings + item.amount || 0,
      }),
      {
        holdings: 0,
      }
    );

    const totalSums = {
      holdings: 0,
    };

    const donutResults: Donut[] = [];

    let otherSum = 0;
    const holdingsData = data.map((item, index) => {
      const currentAmount = item.amount;
      totalSums.holdings += item.amount || 0;
      if (currentAmount) {
        if (index < MAX_FUND_COUNT - 1 && item.categoryType) {
          donutResults.push({
            value: currentAmount,
            color: colors.stocksColors[index],
            symbol: item?.type === CASH_KEY ? item?.type : item?.symbol,
          });
        } else {
          otherSum += currentAmount;
        }
      }

      return {
        id: item.symbol,
        stockQuote: item?.type === CASH_KEY ? item?.type : item?.symbol,
        amount: item.amount,

        isOther: index >= MAX_FUND_COUNT - 1 || !item.categoryType,
        color: index < MAX_FUND_COUNT - 1 ? colors.stocksColors[index] : '',
        percentage: `${item.amount ? _.ceil((item.amount / sums.holdings) * 100, 2) : 0}%`,
      } as InvestmentStockItem;
    });
    donutResults.push({
      value: otherSum,
      color: colors.stocksColors[MAX_FUND_COUNT - 1],
      symbol: 'Other',
    });

    return {
      holdingsData: getGroupedData(holdingsData, totalSums.holdings, true),
      allHoldingsData: getGroupedData(holdingsData, totalSums.holdings, false),
      donutResults: donutResults,
      sums,
    };
  }, [data, getGroupedData]);

  const totalInvestments = useMemo(() => donutData.sums.holdings, [donutData.sums.holdings]);

  const cashlessHoldingAmount = useMemo(
    () =>
      donutData.holdingsData.reduce((result, item) => {
        if (item.stockQuote !== CASH_KEY) {
          return result + item.amount;
        }
        return result;
      }, 0),
    [donutData.holdingsData]
  );

  useEffect(() => {
    setCurrentHoldings(data);
  }, [data, setCurrentHoldings]);

  const getCategoryName = useCallback((key: string) => {
    return _.capitalize(key?.replace(REGEXPS.DASH_SYMBOL, ' ') || '');
  }, []);

  const getHint = useCallback(
    (key: string, value: number, election: number, isCategory?: boolean) => {
      return (
        <Box direction="column" style={{ zIndex: 101 }}>
          <Text size="small" color="textOnColor" truncate weight={400}>
            {isCategory ? getCategoryName(key) : key}
          </Text>
          <Text size="small" color="textOnColor" weight={700}>
            {`${costFormater(value)} (${_.round(election, 2)}%)`}{' '}
          </Text>
        </Box>
      );
    },
    [getCategoryName]
  );

  const formattedByCategory = useMemo(() => {
    const dataMap: ValuesMap<CategoryStockItem[]> = {};
    data.forEach((item, index) => {
      const newItem: CategoryStockItem = {
        key: item.id || '',
        id: item.id || '',
        ticker: item.symbol,
        title: item.name,
        logo: item.image,
        color: item.categoryType
          ? colors.stocksColors[index % 12]
          : colors.stocksColors[MAX_GROUPS_DISPLAYED - 1],
        category: item.categoryType,
        researchURL: item.researchURL,
        value: item.amount,
        election: item.election,

        shares: item.shares,
        sharePrice: item.sharePrice,
      };
      if (dataMap[item.categoryType || NULL_KEY]) {
        dataMap[item.categoryType || NULL_KEY] = [...dataMap[item.categoryType || NULL_KEY], newItem];
      } else {
        dataMap[item.categoryType || NULL_KEY] = [newItem];
      }
    });

    const keys = Object.keys(dataMap);
    const list: DonutGroupItem[] = keys.map((key, index) => {
      const stocks = dataMap[key];
      const { value, election } = stocks.reduce(
        (result, item) => {
          return {
            value: result.value + item.value,
            election: result.election + item.election,
          };
        },
        {
          value: 0,
          election: 0,
        }
      );

      return {
        key: key,
        title: getCategoryName(key),
        value: value,
        election: election,
        items: stocks,
        color:
          key === NULL_KEY
            ? colors.stocksColors[MAX_GROUPS_DISPLAYED - 1]
            : colors.stocksColors[
                index > MAX_GROUPS_DISPLAYED - 1 ? MAX_GROUPS_DISPLAYED - 1 : index % MAX_GROUPS_DISPLAYED
              ],
        hint: getHint(key, value, election, true),
      };
    });
    const sorted = _.sortBy(list, (o) => !o.value);

    return sorted;
  }, [data, getCategoryName, getHint]);

  const formattedByCategoryCut = useMemo(() => {
    const mainGroups = [...formattedByCategory].slice(0, MAX_GROUPS_DISPLAYED);

    const rest = [...formattedByCategory].splice(MAX_GROUPS_DISPLAYED, formattedByCategory.length - 1);
    const othersSum = rest.reduce(
      (result, item) => {
        return {
          value: result.value + (item.value || 0),
          election: result.election + (item.election || 0),
        };
      },
      { value: 0, election: 0 }
    );
    const restItems = rest.reduce((result: CategoryStockItem[], item) => [...result, ...item.items], []);
    const otherItem: DonutGroupItem = {
      key: OTHER_KEY,
      title: 'Others',
      value: othersSum.value,
      election: othersSum.value,
      items: restItems,
      color: colors.stocksColors[7],
      hint: getHint(OTHER_KEY, othersSum.value || 0, othersSum.election || 0),
    };
    const formattedResult: DonutGroupItem[] = [...mainGroups, ...(othersSum.value ? [otherItem] : [])];
    return formattedResult;
  }, [formattedByCategory, getHint]);
  const fundsGroupedByCategories = useMemo(() => {
    const formattedResult = formattedByCategory.reduce((result: CategoryStockItem[], item) => {
      const mappedList = item.items.map((currentItem) => ({
        ...currentItem,
        color: item.color,
        categoryTitle: item.title,
      }));
      return [...result, ...mappedList];
    }, []);
    return formattedResult;
  }, [formattedByCategory]);

  const formattedDonutData = useMemo(() => {
    return donutData.donutResults.map((item) => {
      const total = donutData.sums.holdings;
      const percents = _.round((item.value / total) * 100, 2);
      return {
        key: item.symbol,
        value: item.value,
        color: item.color,
        hint: getHint(item.symbol, item.value, percents),
      };
    });
  }, [donutData.donutResults, donutData.sums.holdings, getHint]);

  const fundsList = useMemo(() => {
    return data.map((item) => {
      const newObject: FundPayload = {
        id: `${item.id}`,
        logo: item.image,
        title: item.name,
        stockCode: item.symbol,
        link: item.researchURL,
        category: item.categoryType || '',
        assetClass: '',
        amount: item.amount,
        election: item.election,
      };

      return newObject;
    });
  }, [data]);
  return {
    isHoldingLoading,
    isSuccessLoaded: isSuccess,
    donutData: formattedDonutData,
    formattedByCategory,
    formattedByCategoryCut,
    details,
    fundsList,
    fundsGroupedByCategories,
    lastDateUpdate,
    data,
    isInvestorAccountExists,
    totalHoldingsAmount: donutData.sums.holdings,

    /** @deprecated */
    isSuccess,
    /** @deprecated */
    donutResults: donutData.donutResults,
    /** @deprecated */
    allHoldingsData: donutData.allHoldingsData,
    /** @deprecated */
    holdingsData: donutData.holdingsData,

    /** @deprecated */
    cashlessHoldingAmount,
    /** @deprecated */
    totalInvestments,
    /** @deprecated */
    formattedList,
  };
};
